/* global React, Icon, Button, Tabs, toast */
// ============================================================
// PLUMME 3 — Privat økonomi (orkestrator)
// Eier privat-shellen (.po-client-layout), årvelger, faner og all
// backend-kobling mot getPersonalFinanceOverview og tilhørende
// mutasjoner. Selve seksjonene ligger i privatokonomi-*.jsx og
// leses fra window ved render.
// ============================================================
const PO_TABS = [
  { icon: 'sparkle', get label() { return t('pkn.main.tab.idag'); }, value: 'idag' },
  { icon: 'wallet', get label() { return t('pkn.main.tab.oversikt'); }, value: 'oversikt' },
  { icon: 'pie', get label() { return t('pkn.main.tab.forbruk'); }, value: 'forbruk' },
  { icon: 'users', get label() { return t('pkn.main.tab.familie'); }, value: 'familie' },
  { icon: 'trendUp', get label() { return t('pkn.main.tab.fremtid'); }, value: 'fremtid' },
  { icon: 'fileText', get label() { return t('pkn.main.tab.reports'); }, value: 'rapporter' },
  { icon: 'wallet', get label() { return t('pkn.main.tab.assets'); }, value: 'eiendeler' },
  { icon: 'card', get label() { return t('pkn.main.tab.gjeld'); }, value: 'gjeld' },
  { icon: 'percent', get label() { return t('pkn.main.tab.skatt'); }, value: 'skatt' },
  { icon: 'upload', get label() { return t('pkn.main.tab.import'); }, value: 'import' },
  { icon: 'receipt', get label() { return t('pkn.main.tab.transaksjoner'); }, value: 'transaksjoner' },
];

const PO_COLORS = ['var(--plum-700)', 'var(--plum-500)', 'var(--plum-400)', 'var(--plum-600)', 'var(--warning)', 'var(--plum-300)', 'var(--success)', 'var(--muted-2)'];
const poColor = (index) => PO_COLORS[index % PO_COLORS.length];
const poNumber = (value) => (value === null || value === undefined ? null : Number(value) || 0);
const poMoney = (value) => `${Math.round(Number(value || 0)).toLocaleString(PlummeI18n.intlLocale())} kr`;
const poMoneyOrMissing = (value) => (value === null || value === undefined ? t('pkn.eie.notRegistered') : poMoney(value));
function poFamilyFeatureEnabled(settings = {}) {
  const features = settings.personalFinanceFeatures || {};
  if (typeof features.family === 'boolean') return features.family;
  const selectedUses = settings.onboardingPreferences?.selectedUses;
  if (Array.isArray(selectedUses) && selectedUses.length) return selectedUses.includes('privat-familie') || selectedUses.includes('familie');
  return true;
}

function poInitials(name) {
  return String(name || 'Privat økonomi').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'P';
}

// Normaliserer readmodel slik at seksjonene alltid får forventet form.
function poFinanceModel(model) {
  return {
    accounts: Array.isArray(model?.accounts) ? model.accounts : [],
    assets: Array.isArray(model?.assets) ? model.assets : [],
    cashflow: model?.cashflow || null,
    categories: Array.isArray(model?.categories) ? model.categories : [],
    dataCoverage: model?.dataCoverage || null,
    debtCategories: Array.isArray(model?.debtCategories) ? model.debtCategories : [],
    debts: Array.isArray(model?.debts) ? model.debts : Array.isArray(model?.loans) ? model.loans : [],
    focus: model?.focus || null,
    health: model?.health || null,
    householdConnection: model?.householdConnection || null,
    insights: Array.isArray(model?.insights) ? model.insights : [],
    jobs: Array.isArray(model?.jobs) ? model.jobs : [],
    monthly: Array.isArray(model?.monthly) ? model.monthly : [],
    planning: model?.planning || { budgets: { custom: [], overrides: {} }, goals: [], household: { members: [] }, lifeEvents: [], plan: null, recurringDecisions: {}, tax: { reviewed: false, situations: [] } },
    recommendations: Array.isArray(model?.recommendations) ? model.recommendations : [],
    recurringPayments: Array.isArray(model?.recurringPayments) ? model.recurringPayments : [],
    savings: Array.isArray(model?.savings) ? model.savings : [],
    summary: model?.summary || {},
    tax: model?.tax || null,
    transactions: Array.isArray(model?.transactions) ? model.transactions : [],
    trends30: Array.isArray(model?.trends30) ? model.trends30 : [],
    typeBreakdown: Array.isArray(model?.typeBreakdown) ? model.typeBreakdown : [],
  };
}

function poAvailableYears(model, currentYear) {
  const years = new Set([Number(currentYear) || new Date().getFullYear()]);
  model.transactions.forEach((transaction) => {
    const year = Number(String(transaction?.date || transaction?.month || '').slice(0, 4));
    if (Number.isFinite(year) && year > 1900) years.add(year);
  });
  const thisYear = new Date().getFullYear();
  [thisYear, thisYear - 1, thisYear - 2].forEach((year) => years.add(year));
  return [...years].sort((a, b) => b - a);
}

// Beløp og endring per forbrukskategori for valgt periode. Periodelogikken
// ligger i window.PoPeriods (src/services/personalFinancePeriods.js).
function poCategoriesWithChange(model, year, period) {
  return window.PoPeriods.categoriesForPeriod(model, year, period, poColor);
}

function poAssets(model) {
  return model.assets.map((asset, index) => ({
    assetType: asset.assetType || 'other',
    category: asset.category || asset.cat || asset.kind || t('pkn.main.asset'),
    color: asset.color || poColor(index),
    detail: asset.detail || asset.description || asset.type || asset.subtitle || '',
    growth: Number(asset.growth || asset.yearlyChange || asset.changePct || 0),
    icon: asset.icon || 'home',
    id: asset.id || asset.name || `asset-${index}`,
    name: asset.name || asset.title || t('pkn.main.asset'),
    taxValue: asset.taxValue === null || asset.taxValue === undefined ? null : Number(asset.taxValue || 0),
    value: Number(asset.value || asset.amount || asset.balance || 0),
  })).filter((asset) => asset.value > 0);
}

function poLoans(model) {
  return model.debts.map((loan, index) => ({
    balance: Number(loan.balance || loan.amount || loan.value || 0),
    category: loan.category || loan.cat || loan.name || t('pkn.main.debt'),
    color: loan.color || poColor(index),
    effectiveRate: loan.effectiveRate === null || loan.effectiveRate === undefined ? null : Number(loan.effectiveRate),
    icon: loan.icon || 'card',
    id: loan.id || loan.name || `debt-${index}`,
    method: loan.method,
    name: loan.name || loan.title || loan.category || t('pkn.gj.loanFallback'),
    observedPayment: Number(loan.observedPayment || 0),
    observedPaymentCount: Number(loan.observedPaymentCount || 0),
    observedPaymentLastMonth: loan.observedPaymentLastMonth || '',
    minimumPayment: Number(loan.minimumPayment || 0),
    original: Number(loan.original || loan.originalAmount || loan.balance || loan.amount || 0),
    rate: Number(loan.rate || loan.interestRate || 0),
    term: Number(loan.term || loan.monthsRemaining || loan.remainingMonths || 240),
    type: loan.type || loan.lender || loan.description || '',
  })).filter((loan) => loan.balance > 0);
}

function poOverviewCards(model) {
  const summary = model.summary;
  return [
    { group: 'formue', icon: 'home', label: t('pkn.main.tab.assets'), tint: 'plum', value: poMoneyOrMissing(poNumber(summary.assetsTotal)) },
    { group: 'formue', icon: 'card', label: t('pkn.main.tab.gjeld'), tint: 'peach', value: poMoneyOrMissing(poNumber(summary.debtTotal)) },
    { group: 'formue', icon: 'trendUp', label: t('pkn.main.netWealth'), tint: 'green', value: poMoneyOrMissing(poNumber(summary.netWorth)) },
    { group: 'flyt', icon: 'arrowUpRight', label: t('pkn.main.in'), tint: 'green', value: poMoney(summary.income) },
    { group: 'flyt', icon: 'receipt', label: t('pkn.main.out'), tint: 'peach', value: poMoney(summary.expenses) },
    { group: 'flyt', icon: 'card', label: t('pkn.main.loanPayments'), tint: 'white', value: poMoney(summary.debtPayments) },
    { group: 'flyt', icon: 'wallet', label: t('pkn.main.savingsInvesting'), tint: 'white', value: poMoney(summary.savings) },
    { group: 'flyt', icon: 'trendUp', label: t('pkn.rpt.netCashflow'), tint: Number(summary.netCashflow || 0) >= 0 ? 'green' : 'peach', value: poMoney(summary.netCashflow) },
  ];
}

function PoClientSwitch({ name, userName }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return <div className="ans-emp-switch po-switch" ref={ref}>
    <button className="ans-emp-btn" onClick={() => setOpen((value) => !value)}>
      <span className="ans-emp-ava">{poInitials(userName)}</span>
      <span className="ans-emp-meta"><span className="ans-emp-name">{name}</span><span className="ans-emp-role">{userName}</span></span>
      <Icon name="chevDown" size={15} />
    </button>
    {open && <div className="ans-emp-menu">
      <div className="ans-emp-menu-h">{t('po.title')}</div>
      <button className="ans-emp-item active">
        <span className="ans-emp-ava sm">{poInitials(userName)}</span>
        <span className="ans-emp-meta"><span className="ans-emp-name">{name}</span><span className="ans-emp-role">{userName}</span></span>
        <Icon name="check" size={16} />
      </button>
    </div>}
  </div>;
}

function PoSidebar({ setRoute, name, userName, view }) {
  return <aside className="ans-side po-side">
    <div className="ans-side-brand">Plumme</div>
    <PoClientSwitch name={name} userName={userName} />
    <div className="ans-side-label">{t('po.title')}</div>
    <nav className="ans-side-nav">
      <button className={`ans-side-item${view === 'home' ? ' active' : ''}`} onClick={() => setRoute?.('privatokonomi')}><Icon name="home" size={18} />{t('nav.home')}</button>
      <button className={`ans-side-item${view === 'profile' ? ' active' : ''}`} onClick={() => setRoute?.('minside')}><Icon name="users" size={18} />{t('nav.myPage')}</button>
    </nav>
  </aside>;
}

function PoBottomNav({ setRoute, view }) {
  const items = [{ key: 'home', route: 'privatokonomi', label: t('nav.home'), icon: 'home' },
    { key: 'profile', route: 'minside', label: t('nav.myPage'), icon: 'users' }];
  return <div className="bnav-wrap">
    <nav className="bnav" aria-label={t('po.title')}>
      {items.map((item) => <button key={item.key} className={`bnav-tab${view === item.key ? ' active' : ''}`} onClick={() => setRoute?.(item.route)}>
        <Icon name={item.icon} size={18} />
        <span>{item.label}</span>
      </button>)}
    </nav>
  </div>;
}

function PoHeader({ advice, availableYears, members, onAsk, onGoBusiness, setTab, setYear, tab, tabs = PO_TABS, tax, userName, year }) {
  const [yearOpen, setYearOpen] = React.useState(false);
  return <React.Fragment>
    <div className="po-top-actions">
      <div className="po-year-wrap">
        <button className="po-year-pill" onClick={() => setYearOpen((open) => !open)}>{year}<Icon name="chevDown" size={15} /></button>
        {yearOpen && <div className="po-year-menu card">
          {availableYears.map((item) => <button key={item} className={item === year ? 'active' : ''} onClick={() => { setYear?.(item); setYearOpen(false); }}>{item}</button>)}
        </div>}
      </div>
      {onGoBusiness && <Button variant="secondary" size="sm" onClick={onGoBusiness} data-help={t('helpTopics.profileScope.body')} data-help-title={t('helpTopics.profileScope.title')}>{t('po.backToBusiness')}</Button>}
    </div>
    <div className="aii-head po-head">
      <div>
        <div className="aii-eyebrow">{t('po.title')}</div>
        <div className="row gap-12" style={{ alignItems: 'center', marginTop: 5 }}>
          <h1 data-help-title={t('po.yourAdvisor')} data-help={t('po.intro')}>{t('po.yourAdvisor')}</h1>
          <span className="po-beta">{t('ap.beta')}</span>
        </div>
        <p>{t('pkn.main.heroIntro')}</p>
      </div>
      {members.length > 1 && <div className="po-person-switch" role="tablist" aria-label={t('po.pickPerson')}>
        {members.map((member) => <button key={member.id} className={`po-person${member.active ? ' on' : ''}`} aria-pressed={member.active}>
          <span className="po-person-ava" style={{ background: member.avatarBg }}>{member.initials}</span>
          <span className="po-person-meta"><span className="po-person-name">{member.name}</span><span className="po-person-role">{member.role}</span></span>
        </button>)}
      </div>}
    </div>
    {tab === 'skatt' && window.PoTaxHero
      ? <window.PoTaxHero actions={tax?.actions} estimate={tax?.estimate} onAsk={onAsk} ruleVersion={tax?.ruleVersion} taxYear={year} />
      : tab !== 'transaksjoner' && window.PoAdvisorHero
        ? <window.PoAdvisorHero advice={advice?.[tab]} onAsk={onAsk} tab={tab} userName={userName} />
        : null}
    <div className="aii-tabs" data-help-title={t('po.sections')} data-help={t('po.sectionsHelp')}>
      <Tabs options={tabs.map((item) => ({ label: item.label, value: item.value }))} value={tab} onChange={setTab} />
    </div>
  </React.Fragment>;
}

function PoHome({ backend, setClient, setRoute, setYear, year }) {
  const [tab, setTab] = React.useState(() => {
    const initial = window.__poTab;
    window.__poTab = null;
    return PO_TABS.some((item) => item.value === initial) ? initial : 'idag';
  });
  const [model, setModel] = React.useState(() => poFinanceModel(null));
  const [dailyAdvice, setDailyAdvice] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [recategorizing, setRecategorizing] = React.useState(false);
  const [error, setError] = React.useState('');
  const [categoryModal, setCategoryModal] = React.useState(null);
  const [chatOpen, setChatOpen] = React.useState(false);
  const [chatSeed, setChatSeed] = React.useState(null);
  const [pendingScrollTarget, setPendingScrollTarget] = React.useState(null);

  const businessId = backend?.activeBusinessId || '';
  const selectedYear = Number(year || backend?.appState?.activeYear) || new Date().getFullYear();
  const userName = backend?.user?.displayName || backend?.user?.email || '';

  const load = React.useCallback(async (targetYear) => {
    if (!backend?.getPersonalFinanceOverview || !businessId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const next = await backend.getPersonalFinanceOverview({ businessId, year: targetYear });
      setModel(poFinanceModel(next));
    } catch (err) {
      setError(err?.message || t('pkn.main.loadFailed'));
      setModel(poFinanceModel(null));
    } finally {
      setLoading(false);
    }
  }, [backend, businessId]);

  React.useEffect(() => { load(selectedYear); }, [load, selectedYear]);

  React.useEffect(() => {
    let cancelled = false;
    setDailyAdvice(null);
    if (!backend?.getPersonalFinanceDailyAdvice || !businessId) return () => { cancelled = true; };
    backend.getPersonalFinanceDailyAdvice({ businessId, year: selectedYear })
      .then((result) => { if (!cancelled) setDailyAdvice(result?.advice || null); })
      // Heroen har lokaliserte standardråd og skal aldri blokkere siden ved AI-feil.
      .catch(() => { if (!cancelled) setDailyAdvice(null); });
    return () => { cancelled = true; };
  }, [backend, businessId, selectedYear]);

  const apply = (result) => setModel(poFinanceModel(result));
  const run = async (label, action) => {
    if (!action) { toast(t('pkn.main.actionUnavailable'), 'error'); return; }
    try {
      const result = await action();
      apply(result);
      if (label) toast(label, 'success');
    } catch (err) {
      const message = err?.message || t('pkn.main.actionFailed');
      setError(message);
      toast(message, 'error');
    }
  };

  // Forbrukssiden viser én måned om gangen. Null betyr «ikke valgt av brukeren»,
  // og da styrer datagrunnlaget: siste måned med importert forbruk.
  const [spendPeriod, setSpendPeriod] = React.useState(null);
  const spendMonths = React.useMemo(() => window.PoPeriods.expenseMonths(model, selectedYear), [model, selectedYear]);
  const spendMonthsKey = spendMonths.join(',');
  React.useEffect(() => {
    setSpendPeriod((current) => (current === null || current === window.PoPeriods.YEAR || spendMonths.includes(Number(current)) ? current : null));
  }, [spendMonths, spendMonthsKey]);
  const activePeriod = spendPeriod === null ? window.PoPeriods.defaultPeriod(spendMonths) : spendPeriod;
  const [previousYearModel, setPreviousYearModel] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    if (Number(activePeriod) !== 1 || !backend?.getPersonalFinanceOverview || !businessId) { setPreviousYearModel(null); return () => { cancelled = true; }; }
    backend.getPersonalFinanceOverview({ businessId, year: selectedYear - 1 })
      .then((value) => { if (!cancelled) setPreviousYearModel(poFinanceModel(value)); })
      .catch(() => { if (!cancelled) setPreviousYearModel(null); });
    return () => { cancelled = true; };
  }, [activePeriod, backend, businessId, selectedYear]);
  const periodModel = React.useMemo(() => {
    if (!previousYearModel) return model;
    const categoriesByName = new Map([...previousYearModel.categories, ...model.categories].map((entry) => [entry.category, entry]));
    return { ...model, categories: [...categoriesByName.values()], transactions: [...previousYearModel.transactions, ...model.transactions] };
  }, [model, previousYearModel]);
  const categories = React.useMemo(() => poCategoriesWithChange(periodModel, selectedYear, activePeriod), [activePeriod, periodModel, selectedYear]);
  const periodMonthCount = React.useMemo(() => window.PoPeriods.monthCount(model, selectedYear, activePeriod), [activePeriod, model, selectedYear]);
  const assets = React.useMemo(() => poAssets(model), [model, PlummeI18n.getLocale()]);
  const loans = React.useMemo(() => poLoans(model), [model, PlummeI18n.getLocale()]);
  const availableYears = React.useMemo(() => poAvailableYears(model, selectedYear), [model, selectedYear]);
  const summary = model.summary;
  const netWorth = poNumber(summary.netWorth);
  const monthlySurplus = Math.max(0, Math.round((Number(summary.income || 0) - Number(summary.expenses || 0)) / 12));
  const familyEnabled = poFamilyFeatureEnabled(backend?.appState?.settings || {});
  const visibleTabs = React.useMemo(() => PO_TABS.filter((item) => item.value !== 'familie' || familyEnabled), [familyEnabled]);
  React.useEffect(() => {
    if (!familyEnabled && tab === 'familie') setTab('idag');
  }, [familyEnabled, tab]);

  const scrollToTarget = React.useCallback((targetId) => {
    if (!targetId) return;
    window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target.classList.remove('po-scroll-focus');
      window.setTimeout(() => target.classList.add('po-scroll-focus'), 20);
      window.setTimeout(() => target.classList.remove('po-scroll-focus'), 1800);
    }, 80);
  }, []);
  const goTab = React.useCallback((next, targetId = null) => {
    if (next === tab) {
      scrollToTarget(targetId);
      return;
    }
    setPendingScrollTarget(targetId);
    setTab(next);
  }, [scrollToTarget, tab]);
  React.useEffect(() => {
    if (!pendingScrollTarget) return;
    scrollToTarget(pendingScrollTarget);
    setPendingScrollTarget(null);
  }, [pendingScrollTarget, scrollToTarget, tab]);
  React.useEffect(() => { window.__poGoTab = goTab; return () => { window.__poGoTab = null; }; }, [goTab]);

  const askChat = (seed) => { setChatSeed(typeof seed === 'string' ? seed : null); setChatOpen(true); };
  const savePlanning = (patch, label) => run(label, backend?.updatePersonalFinancePlanning && (() => backend.updatePersonalFinancePlanning({ businessId, patch, year: selectedYear })));
  const saveInsightStatus = (card, status, snoozedUntil = '') => {
    const next = { ...(model.planning.insightStatus || {}) };
    if (status === 'new') delete next[card.id];
    else next[card.id] = { fingerprint: card.fingerprint || '', snoozedUntil, status, updatedAt: new Date().toISOString() };
    savePlanning({ insightStatus: next }, t('pkn.main.taskUpdated'));
  };
  const saveRecurringDecision = (item, status) => {
    const next = { ...(model.planning.recurringDecisions || {}) };
    next[item.id] = { status, snoozedUntil: status === 'snoozed' ? new Date(Date.now() + 7 * 86400000).toISOString() : '', updatedAt: new Date().toISOString() };
    savePlanning({ recurringDecisions: next }, t('pkn.main.choiceSaved'));
  };
  const openCategory = (entry) => setCategoryModal({
    category: entry.category || entry.name,
    changePct: Number(entry.changePct || 0),
    transactions: (entry.transactions || model.transactions.filter((transaction) => transaction.category === (entry.category || entry.name) && Number(transaction.amount || 0) < 0)),
  });

  const moveTransactions = async ({ category, learn, transactionIds }) => {
    if (!backend?.updatePersonalFinanceTransactionCategory) { toast(t('pkn.main.moveUnavailable'), 'error'); return; }
    try {
      const result = await backend.updatePersonalFinanceTransactionCategory({ businessId, category, learn, transactionIds, type: 'expense', year: selectedYear });
      apply(result);
      setCategoryModal(null);
      toast(learn ? t('pkn.main.movedRuleSaved') : t('pkn.main.moved'), 'success');
    } catch (err) {
      toast(err?.message || t('pkn.main.moveFailed'), 'error');
    }
  };

  const uploadStatement = async (file, account) => {
    if (!backend?.uploadPersonalFinanceBankStatement) { toast(t('pkn.main.importUnavailable'), 'error'); return; }
    setUploading(true);
    setError('');
    try {
      const result = await backend.uploadPersonalFinanceBankStatement({ account, businessId, file });
      const importedYear = Number(String(result.transactions?.[0]?.date || result.transactions?.[0]?.month || '').slice(0, 4));
      const targetYear = Number.isFinite(importedYear) && importedYear > 1900 ? importedYear : selectedYear;
      if (targetYear !== selectedYear) setYear?.(targetYear);
      toast(targetYear !== selectedYear ? t('pkn.main.uploadedShowingYear', { year: targetYear }) : t('pkn.main.uploaded'), 'success');
      await load(targetYear);
    } catch (err) {
      const message = err?.message || t('pkn.main.uploadFailed');
      setError(message);
      toast(message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const recategorize = async () => {
    if (!backend?.recategorizePersonalFinanceTransactions) { toast(t('pkn.main.recatUnavailable'), 'error'); return; }
    setRecategorizing(true);
    setError('');
    try {
      const result = await backend.recategorizePersonalFinanceTransactions({ businessId, year: selectedYear });
      apply(result);
      const rest = [
        result.unchangedTransactions ? t('pkn.main.recatAlreadyRight', { count: result.unchangedTransactions }) : '',
        result.manualTransactions ? t('pkn.main.recatManual', { count: result.manualTransactions }) : '',
        result.uncategorizedTransactions ? t('pkn.main.recatStillOther', { count: result.uncategorizedTransactions }) : '',
      ].filter(Boolean).join(', ');
      toast(`${t('pkn.main.recatDone', { done: result.recategorizedTransactions, total: result.scannedTransactions })}${rest ? `. ${rest}.` : '.'}`, 'success');
    } catch (err) {
      const message = err?.message || t('pkn.main.recatFailed');
      setError(message);
      toast(message, 'error');
    } finally {
      setRecategorizing(false);
    }
  };

  const fetchYear = React.useCallback(async (targetYear) => {
    if (!backend?.getPersonalFinanceOverview || !businessId) return null;
    return poFinanceModel(await backend.getPersonalFinanceOverview({ businessId, year: targetYear }));
  }, [backend, businessId]);

  const body = (() => {
    if (tab === 'idag') return window.PoToday
      ? <React.Fragment><window.PoToday focus={model.focus} health={model.health} insights={model.insights} onAsk={askChat} onGoTab={goTab} onStatus={saveInsightStatus} />
        {window.PoMoneyControl && <window.PoMoneyControl cashflow={model.cashflow} dataCoverage={model.dataCoverage} onSaveBuffer={(buffer) => savePlanning({ buffer }, t('pkn.main.bufferSaved'))} />}</React.Fragment>
      : null;
    if (tab === 'oversikt') return window.PoOverview
      ? <window.PoOverview health={model.health} hasTransactions={model.transactions.length > 0} name={userName} onAsk={askChat}
        overview={poOverviewCards(model)} recommendations={model.recommendations} trends={model.trends30} />
      : null;
    if (tab === 'forbruk') return window.PoForbruk
      ? <window.PoForbruk budgets={model.planning.budgets} categories={categories} onOpenCategory={openCategory} onSaveBudgets={(budgets) => savePlanning({ budgets }, t('pkn.main.budgetSaved'))}
        availableMonths={spendMonths} period={activePeriod} periodMonthCount={periodMonthCount} onPeriodChange={setSpendPeriod} onRecurringDecision={saveRecurringDecision} savings={model.savings} year={selectedYear} />
      : null;
    if (tab === 'transaksjoner') return window.PoTransactions
      ? <window.PoTransactions accounts={model.accounts} categories={[...new Set(model.transactions.map((entry) => entry.category).filter(Boolean))].sort()} onOpenCategory={openCategory} transactions={model.transactions} />
      : null;
    if (tab === 'familie') return window.PoFamilie
      ? <window.PoFamilie connection={model.householdConnection} household={model.planning.household} onAsk={askChat} onSaveHousehold={(household) => savePlanning({ household }, t('pkn.main.householdSaved'))} onOpenHousehold={() => setRoute?.('minside')} />
      : null;
    if (tab === 'fremtid') return window.PoFuture
      ? <window.PoFuture accounts={model.accounts} name={userName} onAsk={askChat} startNet={netWorth || 0} suggestedMonthlySave={monthlySurplus}
        savedPlan={model.planning.plan} savedGoals={model.planning.goals} savedEvents={model.planning.lifeEvents}
        onSavePlanning={(patch) => savePlanning(patch, t('pkn.main.planSaved'))} />
      : null;
    if (tab === 'rapporter') return window.PoReport
      ? <window.PoReport availableYears={availableYears} fetchYear={fetchYear} firstName={String(userName).split(' ')[0]} model={model} onAsk={askChat} onOpenCategory={openCategory} year={selectedYear} setYear={setYear} />
      : null;
    if (tab === 'eiendeler') return window.PoEiendeler
      ? <window.PoEiendeler
        assets={assets}
        cashTotal={poNumber(summary.cashTotal)}
        debtTotal={poNumber(summary.debtTotal) || loans.reduce((sum, loan) => sum + loan.balance, 0)}
        liquidTotal={poNumber(summary.liquidTotal)}
        marketableInvestmentsTotal={poNumber(summary.marketableInvestmentsTotal)}
        netWorth={netWorth}
        onSaveAsset={(asset) => run(t('pkn.main.assetSaved'), backend?.upsertPersonalFinanceAsset && (() => backend.upsertPersonalFinanceAsset({ asset, businessId, year: selectedYear })))}
        onDeleteAsset={(asset) => run(t('pkn.main.assetDeleted'), backend?.deletePersonalFinanceAsset && (() => backend.deletePersonalFinanceAsset({ assetId: asset.id, businessId, year: selectedYear })))}
      />
      : null;
    if (tab === 'gjeld') return window.PoGjeld
      ? <window.PoGjeld
        cashflow={model.cashflow}
        debtPayments={Number(summary.debtPayments || 0)}
        debtTotal={poNumber(summary.debtTotal)}
        loans={loans}
        onSaveLoan={(loan) => run(t('pkn.main.loanSaved'), backend?.upsertPersonalFinanceLoan && (() => backend.upsertPersonalFinanceLoan({ businessId, loan, year: selectedYear })))}
        onDeleteLoan={(loan) => run(t('pkn.main.loanDeleted'), backend?.deletePersonalFinanceLoan && (() => backend.deletePersonalFinanceLoan({ businessId, loanId: loan.id, year: selectedYear })))}
      />
      : null;
    if (tab === 'skatt') return <React.Fragment>
      <div id="po-tax-wizard-target">
        {window.PoTaxWizard && <window.PoTaxWizard deductions={model.tax?.deductions} initialTax={model.planning?.tax || model.tax} rowsByStep={model.tax?.rowsByStep} taxYear={selectedYear} onSave={(tax) => savePlanning({ tax }, t('pkn.main.taxChecklistSaved'))} />}
      </div>
      {window.PoTaxReview && <window.PoTaxReview checks={model.tax?.checks} confidence={model.tax?.confidence} />}
      {window.PoDeductionFinder && <window.PoDeductionFinder deductions={model.tax?.deductions} />}
      {window.PoTaxOpt && <window.PoTaxOpt optimizations={model.tax?.optimizations} taxYear={selectedYear} />}
      {window.PoReadyToFile && <window.PoReadyToFile completed={model.tax?.completed} missing={model.tax?.missing} onGo={goTab} score={model.tax?.confidence || 0} />}
    </React.Fragment>;
    return window.PoImport
      ? <window.PoImport
        accounts={model.accounts}
        error={error}
        jobs={model.jobs}
        loading={loading}
        onAsk={askChat}
        onUpload={uploadStatement}
        onRecategorize={recategorize}
        recategorizing={recategorizing}
        onSaveAccount={(account) => run(t('pkn.main.accountSaved'), backend?.upsertPersonalFinanceAccount && (() => backend.upsertPersonalFinanceAccount({ account, businessId, year: selectedYear })))}
        onDeleteAccount={(account) => run(t('pkn.main.accountDeleted'), backend?.deletePersonalFinanceAccount && (() => backend.deletePersonalFinanceAccount({ accountId: account.id, businessId, year: selectedYear })))}
        onLinkJob={(job, account) => run(t('pkn.main.statementLinked'), backend?.linkPersonalFinanceImportJobAccount && (() => backend.linkPersonalFinanceImportJobAccount({ account, businessId, jobId: job.id, year: selectedYear })))}
        onDeleteJob={(job) => run(t('pkn.main.statementDeleted'), backend?.deletePersonalFinanceImportJob && (() => backend.deletePersonalFinanceImportJob({ businessId, jobId: job.id, year: selectedYear })))}
        summary={summary}
        transactions={model.transactions}
        uploading={uploading}
        year={selectedYear}
      />
      : null;
  })();

  const businessTarget = (backend?.memberships || []).find((membership) => !(membership.clientType === 'personal' || membership.privateEconomy === true)) || null;
  const goBusiness = () => {
    const target = businessTarget;
    if (target?.businessId && setClient) setClient(target.businessId);
    setRoute?.('hjem');
  };

  return <div className="po-content">
    <PoHeader
      advice={dailyAdvice}
      availableYears={availableYears}
      members={[]}
      onAsk={askChat}
      onGoBusiness={businessTarget ? goBusiness : null}
      setTab={setTab}
      setYear={setYear}
      tab={tab}
      tabs={visibleTabs}
      tax={model.tax}
      userName={userName}
      year={selectedYear}
    />
    <div className="aii-page">
      {error && tab !== 'import' && <div className="po-import-error">{error}</div>}
      {body}
    </div>
    {categoryModal && window.PoCatModal && <window.PoCatModal
      category={categoryModal.category}
      categories={categories.map((entry) => entry.category)}
      changePct={categoryModal.changePct}
      transactions={categoryModal.transactions}
      onMove={moveTransactions}
      onClose={() => setCategoryModal(null)}
    />}
    {window.SuperPlummaAdvisorChat && <window.SuperPlummaAdvisorChat open={chatOpen} onClose={() => { setChatOpen(false); setChatSeed(null); }} context="privat" seed={chatSeed}
      ask={(question, chatOptions = {}) => backend?.askPersonalFinanceAssistant
        ? backend.askPersonalFinanceAssistant(question, { businessId, history: chatOptions.history, turnCount: chatOptions.turnCount, year: selectedYear })
        : Promise.reject(new Error(t('pkn.main.assistantUnavailable')))} />}
  </div>;
}

function PrivatOkonomiPage({ route, setClient, setRoute, setYear, year }) {
  const backend = window.PlummeBackend ? window.PlummeBackend.useBackend() : { appState: null, memberships: [], user: null };
  const membership = (backend.memberships || []).find((entry) =>
    entry.businessId === backend.activeBusinessId && (entry.clientType === 'personal' || entry.privateEconomy === true))
    || (backend.memberships || []).find((entry) => entry.clientType === 'personal' || entry.privateEconomy === true)
    || null;
  const name = membership?.businessName || backend.appState?.settings?.businessName || t('pkn.main.personalFinanceName');
  const userName = backend.user?.displayName || backend.user?.email || '';
  const view = route === 'minside' ? 'profile' : 'home';

  return <div className="po-client-layout">
    <PoSidebar view={view} setRoute={setRoute} name={name} userName={userName} />
    <main className="po-workspace">
      {view === 'profile' && window.MinSidePage
        ? <div className="po-profile-wrap"><window.MinSidePage setRoute={setRoute} setClient={setClient} client={backend.activeBusinessId} /></div>
        : <PoHome backend={backend} setClient={setClient} setRoute={setRoute} setYear={setYear} year={year} />}
    </main>
    <PoBottomNav view={view} setRoute={setRoute} />
  </div>;
}

Object.assign(window, { PrivatOkonomiPage, poFinanceModel });
